import React, { useState } from 'react';
import { Box, Button, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import Add from '@mui/icons-material/Add';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import { listEmailTypes, deleteEmailType } from '../../services/EmailTypeService';
import EmailView from './EmailView';
import { getUserData } from '../../utils/FunctionHelpers';
import DialogDeleteCustom from '../../components/DialogDeleteCustom/DialogDeleteCustom';
import { withSnackbar } from '../../utils/SnackbarHOC';
import { useHistory } from 'react-router-dom';
import MessageCellPreview from './MessageCellPreview';
import { commonGridSx, gridContainerSx, gridLocaleText } from '../../utils/gridStyles';
import { GridToolbarContainer, GridToolbarColumnsButton, GridToolbarFilterButton, GridToolbarDensitySelector, GridToolbarQuickFilter } from '@mui/x-data-grid';
import { DataGrid } from '../../components/SortableDataGrid/SortableDataGrid';

const getCurrentRole = () => localStorage.getItem('rolecurrentuser');
const hasAnyRole = (roles) => roles.includes(getCurrentRole());
const CAN_MANAGE = hasAnyRole(['ROLE_DIRECTION', 'ROLE_TALENT_ACQUISITION_MANAGER']);

const EmailTypeList = ({ snackbarShowMessage }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialogDelete, setOpenDialogDelete] = useState(false);
  const [messageInfos, setMessageInfos] = useState({ uuid: '', msgName: '' });
  const [loadingToDelete, setLoadingToDelete] = useState(false);
  const [reloadData, setReloadData] = useState(false);
  const history = useHistory();

  React.useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setMessages([]);
      try {
        const res = await listEmailTypes();
        if (!active) return;
        setMessages(res.data || []);
      } catch {
        snackbarShowMessage('Erreur de chargement des email types!', 'error', 4000);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [reloadData]);

  const handleOpenDialog = (row) => {
    setOpenDialogDelete(true);
    setMessageInfos({ uuid: row.uuid, msgName: row.activite });
  };

  const handleCloseDialog = () => { if (!loadingToDelete) setOpenDialogDelete(false); };

  const handleDeleteEmailType = async () => {
    setLoadingToDelete(true);
    try {
      snackbarShowMessage('Patientez svp!', 'warning', null);
      const res = await deleteEmailType(messageInfos.uuid);
      snackbarShowMessage(res.data, 'success', 4000);
      handleCloseDialog();
      setReloadData(!reloadData);
    } catch (error) {
      snackbarShowMessage('Une erreur est survenue: ' + error.message, 'error', 4000);
    } finally {
      setLoadingToDelete(false);
    }
  };

  const CustomToolbar = () => (
    <GridToolbarContainer sx={{ px: 2, py: 1.25, borderBottom: '1px solid #f1f5f9', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
      <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#0f172a', mr: 'auto' }}>Messages Types</Typography>
      <Button startIcon={<Add sx={{ fontSize: 16 }} />} onClick={() => history.push('/emailtype/add')}
        sx={{ textTransform: 'none', fontWeight: 500, fontSize: '1rem', color: '#03407b', border: '1px solid #aed6f1', borderRadius: 2, px: 2, py: 0.75, background: '#ebf4fb', '&:hover': { background: '#d6eaf8', borderColor: '#7fb3d3' } }}>
        Ajouter
      </Button>
      <GridToolbarQuickFilter
        sx={{
          '& .MuiInputBase-root': { borderRadius: 2, fontSize: '1rem', background: '#f8fafc', px: 1.5, height: 36 },
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
          '& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#7fb3d3' },
          '& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#03407b' },
        }}
        placeholder="Rechercher..."
        debounceMs={400}
      />
      <GridToolbarColumnsButton sx={{ textTransform: 'none', fontSize: '1rem', color: '#64748b', borderRadius: 2 }} />
      <GridToolbarFilterButton sx={{ textTransform: 'none', fontSize: '1rem', color: '#64748b', borderRadius: 2 }} />
      <GridToolbarDensitySelector sx={{ textTransform: 'none', fontSize: '1rem', color: '#64748b', borderRadius: 2 }} />
    </GridToolbarContainer>
  );

  const typeMsgColors = {
    email: { bg: '#dbeafe', color: '#1e40af', border: '#bfdbfe' },
    sms: { bg: '#f0fdf4', color: '#166534', border: '#bbf7d0' },
    whatsapp: { bg: '#dcfce7', color: '#15803d', border: '#86efac' },
  };

  const columns = [
    {
      field: 'activite', headerName: 'ACTIVITÉ', flex: 0.8, minWidth: 130,
      renderCell: (p) => (
        <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: '#0f172a', textTransform: 'capitalize', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {p.value || '—'}
        </Typography>
      ),
    },
    {
      field: 'TypeMsg', headerName: 'TYPE', width: 100,
      renderCell: (p) => {
        const key = (p.value || '').toLowerCase();
        const cfg = typeMsgColors[key] || { bg: '#f1f5f9', color: '#475569', border: '#e2e8f0' };
        return (
          <Chip label={p.value || '—'} size="small"
            sx={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`, fontWeight: 600, fontSize: '0.8125rem', textTransform: 'uppercase', height: 22 }} />
        );
      },
    },
    {
      field: 'msgName', headerName: 'INTITULÉ', flex: 1, minWidth: 160,
      renderCell: (p) => (
        <Typography sx={{ fontSize: '1rem', color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {p.value || '—'}
        </Typography>
      ),
    },
    {
      field: 'objet', headerName: 'OBJET DU MAIL', flex: 1, minWidth: 160,
      renderCell: (p) => (
        <Typography sx={{ fontSize: '1rem', color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {p.value || '—'}
        </Typography>
      ),
    },
    {
      field: 'message', headerName: 'CONTENU', flex: 1.4, minWidth: 200, sortable: false,
      renderCell: (p) => p.value ? (
        <Box sx={{ width: '100%', overflow: 'hidden' }}>
          <MessageCellPreview html={p.value} />
        </Box>
      ) : <Typography sx={{ color: '#cbd5e1', fontSize: '1rem' }}>—</Typography>,
    },
    {
      field: 'actions_col', headerName: 'ACTIONS', width: 130, sortable: false, filterable: false,
      renderCell: (p) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
          <EmailView
            activite={p.row.activite}
            msgName={p.row.msgName}
            objet={p.row.objet}
            message={p.row.message}
            TypeMsg={p.row.TypeMsg}
          />
          <Tooltip title="Modifier">
            <IconButton size="small" onClick={() => history.push(`/emailtype/${p.row.uuid}/edit`)}
              sx={{ color: '#eab308', background: '#fefce8', borderRadius: '8px', '&:hover': { background: '#fef9c3' }, p: 0.75 }}>
              <Edit sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          {CAN_MANAGE && getUserData().uuid !== p.row.uuid && (
            <Tooltip title="Supprimer">
              <IconButton size="small" onClick={() => handleOpenDialog(p.row)}
                sx={{ color: '#ef4444', background: '#fef2f2', borderRadius: '8px', '&:hover': { background: '#fee2e2' }, p: 0.75 }}>
                <Delete sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  const rows = (messages || []).map((m) => ({ ...m, id: m.uuid }));

  return (
    <Box sx={{ px: '20px', pb: 2, backgroundColor: '#f1f5f9', minHeight: '100vh' }}>
      <Typography sx={{ pt: 2, pb: 1.5, color: '#0f172a', fontFamily: "'Fira Sans', sans-serif", fontWeight: 700, fontSize: '1.5rem', letterSpacing: '-0.01em' }}>
        MESSAGES TYPE
      </Typography>
      <Box sx={{ ...gridContainerSx, height: 'calc(100vh - 110px)' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.uuid}
          loading={loading}
          pageSizeOptions={[50, 100]}
          disableRowSelectionOnClick
          showToolbar
          slots={{ toolbar: CustomToolbar }}
          slotProps={{ loadingOverlay: { variant: 'skeleton', noRowsVariant: 'skeleton' } }}
          localeText={gridLocaleText}
          sx={commonGridSx}
        />
      </Box>
      <DialogDeleteCustom
        handleClose={handleCloseDialog}
        open={openDialogDelete}
        handleConfirm={handleDeleteEmailType}
        loading={loadingToDelete}
        label="Voulez vous vraiment supprimer ce message ?"
        title="Supprimer le message"
      />
    </Box>
  );
};

export default withSnackbar(EmailTypeList);
